import { Box, Stack } from "@mui/material"
import { CreateScheduleButton } from "../components/create-schedule-button"
import { ScopedSchedulesButton } from "../components/scoped-schedules-button"
import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import { ScopedScheduleRow } from "./scoped-schedule-row"
import { useParams } from "react-router"
import { If } from "lib/@components/switch"
import { useCallback, useEffect, useMemo, useState } from "react"
import { SelectAllCheckbox } from "../group/lib/select-all-checkbox"
import { Bound } from "lib/@components/binding/Bound"
import GenericSearch from "lib/@components/paging/generic-search"
import { ResetTabledRepeat } from "event-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { Else } from "lib/@components/else"
import { ExtendedNoResults } from "../group/extended-no-results"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { getDraftOrPublishedSchedules } from "../controller/get-draft-or-published-schedules"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { collectMacroTokensFromSchedule } from "authoring/routes/macro/lib/macro-token-utils"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { MacroExpander } from "authoring/routes/macro/components/macro-expander"
import { SelectedItemsBanner } from "../components/selected-items-banner"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { useDialog } from "lib/@hooks/useDialog"
import { deleteScopedSchedules } from "../controller/delete-scoped-schedules"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { DeleteScopedSchedulesDialog } from "../components/delete-scoped-schedules-dialog"
import { compareByCode } from "authoring/lib/compare-by-code"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import {
    AUTHORING_CREATE_SCHEDULES_DEMAND,
    AUTHORING_EDIT_SCHEDULES_DEMAND,
} from "authoring/lib/constants/authoring-permissions.mjs"

const tableHeaders = [
    {
        title: <SelectAllCheckbox />,
    },
    { title: "Schedule" },
    { title: "Code" },
    { title: "Tasks" },
    { title: "Status" },
    { title: "" },
    { title: "" },
    { title: "" },
]

export function SchedulesTab() {
    const { projectId } = useParams()
    const { target } = useBoundContext()
    const scheduleIds = target?.scopedScheduleIds ?? []
    const schedulesResult = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const schedulesLoading = schedulesResult === IS_LOADING
    const schedules = schedulesLoading ? undefined : schedulesResult

    const createdSchedules = (schedules ?? []).filter((schedule) => schedule?.seedSource === "new")
    const createdScheduleIds = createdSchedules?.map(({ id }) => id)

    const tokenIds = useMemo(collectAllTokenIds, [schedules])
    const { macroMap } = useEnrichedMacroMap({ tokenIds })

    const [selectedSchedules, setSelectedSchedules] = useState({})
    const [search, setSearch] = useState("")
    const selectedScheduleIds = Object.keys(selectedSchedules)
    const selectedCount = selectedScheduleIds.length
    const selectedSchedulesForDialog = getSelectedSchedules({ schedules, selectedScheduleIds })
    const disableDelete = isLockedToLegacy(target?.phase)

    const deleteSchedulesDialog = useDialog(
        <DeleteScopedSchedulesDialog selectedSchedules={selectedSchedulesForDialog} />,
        {
            maxWidth: "md",
            fullWidth: true,
        }
    )
    const matchesSearch = useCallback(matchSearch, [search])
    const filteredSchedules = useMemo(getFilteredSchedules, [matchesSearch, schedules])

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [search, schedulesResult])

    if (schedulesLoading) return <LoadingScreen />

    return (
        <Bound
            filteredItems={filteredSchedules ?? []}
            selectedKey="id"
            selectedItems={selectedSchedules}
            setSelectedItems={setSelectedSchedules}
            macroMap={macroMap}
            createdScheduleIds={createdScheduleIds}
        >
            <Box>
                <Stack direction="row" sx={{ justifyContent: "space-between", mb: 3 }}>
                    <Stack direction="row" spacing={1}>
                        <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />}>
                            <ScopedSchedulesButton />
                        </Secure>
                        <Secure
                            demand={[AUTHORING_CREATE_SCHEDULES_DEMAND, AUTHORING_EDIT_SCHEDULES_DEMAND]}
                            fallback={<Frag />}
                        >
                            <CreateScheduleButton />
                        </Secure>
                    </Stack>
                    <GenericSearch
                        results={schedules}
                        searchPlaceHolder="Search"
                        search={search}
                        setSearch={setSearch}
                        showResultCount={false}
                        size="small"
                    />
                </Stack>
                <If truthy={schedules?.length > 0}>
                    <If truthy={selectedCount}>
                        <SelectedItemsBanner
                            selected={selectedScheduleIds}
                            label={"schedule"}
                            handleDelete={handleDelete}
                            disabled={disableDelete}
                        />
                    </If>
                    <MacroExpander>
                        <TabledRepeat
                            items={filteredSchedules}
                            itemPass="schedule"
                            itemComponent={<ScopedScheduleRow />}
                            tableHeaders={tableHeaders}
                            paged
                            alternating={false}
                            sx={{ "& tr td.MuiTableCell-root:last-of-type": { px: 0 } }}
                        />
                    </MacroExpander>
                    <Else>
                        <ExtendedNoResults
                            message="There are no schedules added to your work package yet. Start by adding one."
                            component={
                                <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />}>
                                    <ScopedSchedulesButton variant="outlined" />
                                </Secure>
                            }
                        />
                    </Else>
                </If>
            </Box>
        </Bound>
    )

    function collectAllTokenIds() {
        const tokenSet = new Set()
        for (const schedule of schedules ?? []) collectMacroTokensFromSchedule(schedule, tokenSet)
        return [...tokenSet]
    }

    async function handleDelete(scheduleIdsToDelete) {
        try {
            const confirmDelete = await deleteSchedulesDialog()
            if (!confirmDelete) return

            const deleted = await deleteScopedSchedules({
                projectId,
                scheduleIds: scheduleIdsToDelete,
            })

            if (!deleted) {
                errorSnackbar("Failed to remove selected schedules")
                return
            }

            setSelectedSchedules({})
            successSnackbar(
                `Successfully removed ${scheduleIdsToDelete.length} ${pluralizeSchedule(scheduleIdsToDelete)}`
            )
        } catch (error) {
            console.error(error)
            errorSnackbar("Failed to remove selected schedules")
        }
    }

    function matchSearch({ title, code }) {
        const searchLower = search.trim().toLowerCase()
        if (!searchLower) return true
        return title?.toLowerCase()?.includes(searchLower) || code?.toLowerCase()?.includes(searchLower)
    }

    function getFilteredSchedules() {
        return schedules?.filter(matchesSearch)?.sort(compareByCode)
    }
}

function getSelectedSchedules({ schedules = [], selectedScheduleIds = [] }) {
    const selectedIdSet = new Set(selectedScheduleIds)

    return (schedules ?? []).filter(({ id }) => selectedIdSet.has(id))
}

function pluralizeSchedule(scheduleIds) {
    return scheduleIds.length === 1 ? "schedule" : "schedules"
}
